import ChartFrame from './ChartFrame'
import CopyEndpoint from '../ui/CopyEndpoint'
import DataTable from '../ui/DataTable'
import { formatLatency } from '../lib/format'
import { doh } from '../content/site'
import type { Probe } from '../hooks/useDohSpeed'
import styles from './LatencyRanking.module.css'

interface LatencyRankingProps {
    probes: Probe[]
    onRemoveCustom?: (url: string) => void
}

/*
 * What we can honestly say about a failure. The browser distinguishes an aborted
 * request (our own 3s timeout) from a failed connection, but deliberately hides
 * whether a failed connection was a network block, a refused port, or a CORS
 * rejection -- so those are named together rather than guessed apart.
 */
const REASON: Record<NonNullable<Probe['reason']>, string> = {
    timeout: 'no response within 3s',
    refused: 'connection refused, blocked, or CORS-restricted',
}

const STATUS_LABEL: Record<Probe['status'], string> = {
    pending: 'testing…',
    ok: '',
    failed: '',
    unreachable: doh.unreachable,
}

/**
 * Providers ranked fastest first.
 *
 * Only resolvers that actually answered are listed, plus every resolver the
 * visitor added themselves. A published endpoint being blocked on this network
 * says nothing about the visitor's DNS setup -- it is our list being unreachable,
 * not their configuration being wrong -- and a dozen identical failure rows
 * reads as a broken page rather than as a finding. The count that did answer is
 * kept in the header so the omission is stated rather than hidden.
 *
 * A custom resolver is the opposite case: the visitor typed it in and needs to
 * know precisely why it did not work. Those always appear, with the reason, and
 * unreachable stays distinct from failed -- a resolver answering SERVFAIL is
 * reachable but unhappy, while an unreachable one is almost certainly blocked.
 */
export default function LatencyRanking({ probes, onRemoveCustom }: LatencyRankingProps) {
    const measured = probes.filter((p) => p.status === 'ok' && p.latencyMs !== undefined)
    const ranked = [...measured].sort((a, b) => (a.latencyMs as number) - (b.latencyMs as number))
    const slowest = ranked[ranked.length - 1]?.latencyMs ?? 1

    // the visitor's own resolvers, whatever happened to them
    const custom = probes.filter((p) => p.custom && p.status !== 'ok')
    // published endpoints that did not answer: counted, not enumerated
    const silent = probes.filter((p) => !p.custom && p.status !== 'ok' && p.status !== 'pending')
    const testing = probes.filter((p) => !p.custom && p.status === 'pending')
    const listed = [...ranked, ...custom]

    return (
        <ChartFrame
            title="DoH latency"
            note="Measured from your browser, one sample each, including the TLS handshake. Relative, not absolute."
            aside={`${measured.length}/${probes.length} answered`}
            table={
                <DataTable
                    caption="Measured latency per DoH provider"
                    columns={[
                        { key: 'provider', header: 'Provider', render: (p: Probe) => p.provider },
                        { key: 'url', header: 'Endpoint',
                          render: (p: Probe) => (
                              <span className={styles.endpointCell}>
                                  {p.url}
                                  <CopyEndpoint url={p.url} name={p.provider} />
                              </span>
                          ) },
                        { key: 'latency', header: 'Latency', numeric: true,
                          render: (p: Probe) => (p.status === 'ok' ? formatLatency(p.latencyMs) : '—') },
                        { key: 'status', header: 'Result',
                          render: (p: Probe) => p.answer ?? STATUS_LABEL[p.status] },
                    ]}
                    rows={listed}
                    rowKey={(p) => p.url}
                />
            }
        >
            <ul className={styles.list}>
                {ranked.map((probe) => (
                    <li key={probe.url} className={styles.row}>
                        <span className={styles.label}>
                            <span className={styles.name}>{probe.provider}</span>
                            {probe.custom && <span className={styles.tag}>custom</span>}
                            <CopyEndpoint url={probe.url} name={probe.provider} />
                            {probe.custom && onRemoveCustom && (
                                <button type="button" className={styles.remove}
                                    onClick={() => onRemoveCustom(probe.url)}
                                    aria-label={`Remove ${probe.provider}`}>✕</button>
                            )}
                        </span>
                        <span className={styles.track}>
                            <span className={styles.bar}
                                style={{ width: `${((probe.latencyMs as number) / slowest) * 100}%` }} />
                        </span>
                        <span className={styles.value}>{formatLatency(probe.latencyMs)}</span>
                    </li>
                ))}

                {custom.map((probe) => (
                    <li key={probe.url} className={styles.row}>
                        <span className={styles.label}>
                            <span className={styles.name}>{probe.provider}</span>
                            {probe.custom && <span className={styles.tag}>custom</span>}
                            <CopyEndpoint url={probe.url} name={probe.provider} />
                            {probe.custom && onRemoveCustom && (
                                <button type="button" className={styles.remove}
                                    onClick={() => onRemoveCustom(probe.url)}
                                    aria-label={`Remove ${probe.provider}`}>✕</button>
                            )}
                        </span>

                        {probe.status === 'pending' ? (
                            <span className={styles.pending}>{STATUS_LABEL.pending}</span>
                        ) : probe.status === 'unreachable' ? (
                            <span className={styles.unreachable}>
                                — {doh.unreachable}
                                {probe.reason && (
                                    <span className={styles.reason}>— {REASON[probe.reason]}</span>
                                )}
                            </span>
                        ) : (
                            <span className={styles.failed}>
                                ✕ {probe.answer ?? 'SERVFAIL'}
                                <span className={styles.reason}>— answered but refused</span>
                            </span>
                        )}

                        <span />
                    </li>
                ))}
            </ul>

            {testing.length > 0 && (
                <p className={styles.progress}>Testing {testing.length} more…</p>
            )}

            {/* Stated by default, enumerated on request. Blocked endpoints are a
                fact about this network, not the visitor's DNS configuration, so
                the list stays folded away unless someone wants the detail. */}
            {testing.length === 0 && silent.length > 0 && (
                <details className={styles.silentDetails}>
                    <summary className={styles.silent}>
                        {silent.length} other {silent.length === 1 ? 'resolver' : 'resolvers'} did not
                        answer from your network, and {silent.length === 1 ? 'is' : 'are'} not listed.
                    </summary>
                    <ul className={styles.reasons}>
                        {silent.map((probe) => (
                            <li key={probe.url}>
                                <span className={styles.reasonName}>{probe.provider}</span>
                                <span className={styles.reasonWhy}>
                                    {probe.status === 'failed'
                                        ? `answered ${probe.answer ?? 'SERVFAIL'}`
                                        : probe.reason ? REASON[probe.reason] : 'did not answer'}
                                </span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {listed.length === 0 && testing.length === 0 && (
                <p className={styles.silent}>
                    No resolver answered from your network. That usually means DNS-over-HTTPS
                    is blocked here rather than that anything is wrong with your setup.
                </p>
            )}
        </ChartFrame>
    )
}
